// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type { BankUploadStep } from '@client/banks/types/bank-upload';
import { selectHasBankUploadDraft } from '@shared/data-access/slices/clients/clients-selectors';
import { useStepsData } from '@shared/ui-kit/hooks/use-steps';
import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';

export type BankUploadFlowOption = 'wealthreader' | 'manual';

const FLOW_NEXT_STEP: Record<BankUploadFlowOption, BankUploadStep> = {
  wealthreader: 'connect-external',
  manual: 'institution-manual',
};

export const useFlowSelect = () => {
  const { navigate } = useStepsData();
  const hasDraft = useSelector(selectHasBankUploadDraft);
  const [selectedFlow, setSelectedFlow] = useState<BankUploadFlowOption | null>(
    () => (hasDraft ? 'manual' : null),
  );

  const onSelectFlow = useCallback((flow: BankUploadFlowOption) => {
    setSelectedFlow(flow);
  }, []);

  const onContinue = useCallback(() => {
    if (!selectedFlow) {
      return false;
    }

    navigate(FLOW_NEXT_STEP[selectedFlow]);

    return true;
  }, [navigate, selectedFlow]);

  return {
    hasDraft,
    selectedFlow,
    onSelectFlow,
    onContinue,
    isContinueDisabled: selectedFlow === null,
  };
};
